// frontend/src/components/orcamento/AbaPremissas.js
// Aba Premissas do Orçamento com Monte Carlo (23/10/2026): 4 blocos (Pessoal,
// Receita e estrutura, Tributário e sócios, Eventos incertos). Cada mudança vai
// para um rascunho local na hora; ~600 ms depois o rascunho inteiro é gravado
// (POST) e a projeção é refeita (GET). No topo, o resultado P10/P50/P90 e a
// chance de prejuízo ficam à vista para ver o efeito enquanto mexe.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ConfirmarModal from '../ConfirmarModal';
import { parseBR, ehSintetica } from './useProjecao';

const ESPERA = 600;          // ms do debounce
const EVENTOS_MAX = 20;
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const SEM = 'Sem departamento';

const num = (v, casas = 1) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
const sinal = (v, casas = 1) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${num(Math.abs(v), casas)}`;
const reais = (v) => `${v < 0 ? '−' : ''}${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(Math.abs(v || 0))}`;
const reais2 = (v) => (v === null || v === undefined || v === '') ? '' :
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
const mi = (v) => (!Number.isFinite(v) ? '—' : `${v < 0 ? '−' : ''}R$ ${(Math.abs(v) / 1e6).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} mi`);

// cópia profunda simples (o rascunho só tem números, textos, listas e objetos)
const copiar = (o) => JSON.parse(JSON.stringify(o || {}));
// padrões (iguais aos do backend): payload sem `premissas` não quebra os controles
const PADRAO = {
  dissidio_min: 0, dissidio_max: 0, data_base_mes: 1, pessoal_acompanha_receita: 0, inflacao_administrativas: 0,
  reajuste_receita: 0, recebimento_abaixo: 0, crescimento_mes: 0, custo_extra_pp: 0, valores_fixos: {},
  investimentos_ano: 0, eventos: [], grupos: { pessoal: null, administrativas: null },
};
const comPadrao = (p) => ({ ...copiar(PADRAO), ...copiar(p) });

// rascunho → corpo do POST (todas as chaves, números de verdade)
function normalizar(r) {
  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  return {
    dissidio_min: n(r.dissidio_min), dissidio_max: n(r.dissidio_max), data_base_mes: parseInt(r.data_base_mes, 10) || 1,
    pessoal_acompanha_receita: n(r.pessoal_acompanha_receita), inflacao_administrativas: n(r.inflacao_administrativas),
    reajuste_receita: n(r.reajuste_receita), recebimento_abaixo: n(r.recebimento_abaixo), crescimento_mes: n(r.crescimento_mes),
    custo_extra_pp: n(r.custo_extra_pp), investimentos_ano: n(r.investimentos_ano),
    valores_fixos: Object.fromEntries(Object.entries(r.valores_fixos || {}).map(([c, v]) => [c, n(v)])),
    eventos: (r.eventos || []).map((e) => ({
      nome: String(e.nome || '').trim(), natureza: e.natureza === 'receita' ? 'receita' : 'despesa',
      valor_mes: n(e.valor_mes), mes_inicial: parseInt(e.mes_inicial, 10) || 1, chance: n(e.chance), pilar: e.pilar || '',
    })),
    grupos: { pessoal: r.grupos?.pessoal ?? null, administrativas: r.grupos?.administrativas ?? null },
  };
}

// o que impede de gravar antes de ir ao servidor: {campo, erro} ou null
function checarLocal(r) {
  if (Number(r.dissidio_min) > Number(r.dissidio_max)) return { campo: 'dissidio_min', erro: 'O mínimo não pode passar do máximo.' };
  const i = (r.eventos || []).findIndex((e) => !String(e.nome || '').trim());
  if (i >= 0) return { campo: `eventos[${i}].nome`, erro: 'Dê um nome ao evento para gravar.' };
  return null;
}

// campo em R$: texto livre enquanto digita, vira número ao sair ou no Enter
function CampoReais({ valor, onMudar, rotulo, desabilitado }) {
  const [txt, setTxt] = useState(reais2(valor));
  const [foco, setFoco] = useState(false);
  useEffect(() => { if (!foco) setTxt(reais2(valor)); }, [valor, foco]);
  const confirmar = () => {
    const v = parseBR(txt);
    if (v === null || v < 0) { setTxt(reais2(valor)); return; }
    if (v !== valor) onMudar(v);
    setTxt(reais2(v));
  };
  return (
    <div className="op-prem-reais">
      <span>R$</span>
      <input className="pa-input" value={txt} aria-label={rotulo} disabled={desabilitado} inputMode="decimal"
        onChange={(e) => setTxt(e.target.value)} onFocus={(e) => { setFoco(true); e.target.select(); }}
        onBlur={() => { setFoco(false); confirmar(); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />
    </div>
  );
}

// slider: rótulo à esquerda, valor à direita (números tabulares), contexto embaixo
function Slider({ rotulo, valor, min, max, passo, unidade, casas = 1, comSinal = false, contexto, erro, onMudar }) {
  const txt = `${comSinal ? sinal(valor, casas) : num(valor, casas)} ${unidade}`;
  return (
    <div className="op-prem-campo">
      <div className="op-prem-linha"><label>{rotulo}</label><span className="op-prem-valor">{txt}</span></div>
      <input type="range" className="op-prem-range" min={min} max={max} step={passo} value={valor}
        aria-label={rotulo} aria-valuetext={txt} onChange={(e) => onMudar(Number(e.target.value))} />
      {contexto && <div className="op-prem-ctx">{contexto}</div>}
      {erro && <div className="op-prem-erro">{erro}</div>}
    </div>
  );
}

// "afeta N categorias · ajustar" + painel com a lista de despesas para marcar
function Grupo({ titulo, n, categorias, marcados, onSalvar }) {
  const [aberto, setAberto] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [filtro, setFiltro] = useState('');
  const abrir = () => { setSel(new Set(marcados)); setFiltro(''); setAberto(true); };
  const lista = categorias.filter((c) => !filtro || `${c.codigo} ${c.nome}`.toLowerCase().includes(filtro.toLowerCase()));
  return (
    <div className="op-prem-grupo">
      <span>afeta {n} {n === 1 ? 'categoria' : 'categorias'} · </span>
      <button type="button" className="op-prem-link" onClick={abrir}>ajustar</button>
      {aberto && (
        <div className="op-prem-pop" role="dialog" aria-label={`Categorias de ${titulo}`}>
          <div className="op-prem-pop-head">
            <b>Categorias de {titulo}</b>
            <button type="button" className="pa-icon-btn" onClick={() => setAberto(false)} aria-label="Fechar"><i className="fas fa-times"></i></button>
          </div>
          <input className="pa-input mb-2" placeholder="filtrar…" value={filtro} onChange={(e) => setFiltro(e.target.value)} />
          <div className="op-prem-pop-lista">
            {lista.map((c) => (
              <label key={c.codigo} className="op-prem-check">
                <input type="checkbox" checked={sel.has(c.codigo)} onChange={() => setSel((s) => {
                  const t = new Set(s); if (t.has(c.codigo)) t.delete(c.codigo); else t.add(c.codigo); return t;
                })} />
                <span className="cod">{c.codigo}</span>{c.nome}
              </label>
            ))}
          </div>
          <div className="op-prem-pop-pe">
            <button type="button" className="pa-btn pa-btn-ghost" onClick={() => { onSalvar(null); setAberto(false); }}>Voltar ao automático</button>
            <button type="button" className="pa-btn pa-btn-primary" onClick={() => { onSalvar([...sel]); setAberto(false); }}>
              Usar {sel.size} {sel.size === 1 ? 'categoria' : 'categorias'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Ícone "O que é esta premissa": abre um popover com o que é, onde influencia e
// como entra no cálculo. Só um aberto por vez (estado `aberto` vem do pai);
// fecha com Esc ou clique fora.
function Info({ id, aberto, onAlternar, onFechar, texto }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!aberto) return undefined;
    const tecla = (e) => { if (e.key === 'Escape') onFechar(); };
    const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) onFechar(); };
    window.addEventListener('keydown', tecla);
    document.addEventListener('mousedown', fora);
    return () => { window.removeEventListener('keydown', tecla); document.removeEventListener('mousedown', fora); };
  }, [aberto, onFechar]);
  return (
    <span className="op-prem-info" ref={ref}>
      <button type="button" className="op-prem-info-btn" aria-label="O que é esta premissa" aria-expanded={aberto}
        aria-controls={`op-prem-info-${id}`} onClick={onAlternar}>
        <i className="fas fa-circle-info" aria-hidden="true"></i>
      </button>
      {aberto && (
        <div className="op-prem-info-pop" id={`op-prem-info-${id}`} role="dialog" aria-label="Sobre esta premissa">
          {texto.oque && <><div className="pa-eyebrow">O que é</div><p>{texto.oque}</p></>}
          {texto.onde && <><div className="pa-eyebrow">Onde influencia</div><p>{texto.onde}</p></>}
          {texto.como && <><div className="pa-eyebrow">Como entra no cálculo</div><p>{texto.como}</p></>}
        </div>
      )}
    </span>
  );
}

// "as 18 categorias de Pessoal (Salários, INSS/FGTS, Vale Refeição…)"
function descreverGrupo(nome, codigos, nomeCat) {
  const n = (codigos || []).length;
  if (!n) return `as categorias de ${nome} (nenhuma marcada ainda — use "ajustar")`;
  const exemplos = codigos.slice(0, 3).map((c) => nomeCat[c] || c).join(', ');
  return `${n === 1 ? 'a categoria' : `as ${n} categorias`} de ${nome} (${exemplos}${n > 3 ? '…' : ''})`;
}

// Textos da ajuda por premissa; `ctx` traz os grupos reais e os nomes das categorias
function textosAjuda(ctx) {
  const pessoal = descreverGrupo('Pessoal', ctx.pessoal, ctx.nomeCat);
  const adm = descreverGrupo('Administrativas', ctx.administrativas, ctx.nomeCat);
  const tela = 'Na tela, mudam a grade, o resultado no topo, a Simulação e os pilares.';
  return {
    bloco_pessoal: { oque: 'Premissas sobre a folha: o reajuste anual e quanto o time cresce junto com a receita.' },
    bloco_receita: { oque: 'Premissas sobre as receitas (contratos, preço, inadimplência) e sobre a inflação das despesas administrativas.' },
    bloco_tributario: { oque: 'Premissas de impostos sobre a receita, retiradas dos sócios e investimentos do ano.' },
    bloco_eventos: { oque: 'Coisas que podem ou não acontecer no ano (um contrato novo, uma multa): cada cenário sorteia se ocorrem.' },
    dissidio: {
      oque: 'O reajuste salarial anual da categoria (dissídio ou convenção coletiva).',
      onde: `Influencia ${pessoal}, a partir do mês da data-base. ${tela}`,
      como: 'Cada cenário sorteia um percentual entre o mínimo e o máximo e aplica sobre essas categorias; por isso o P50 sobe um pouco e a faixa P10–P90 alarga.',
    },
    acompanha: {
      oque: 'Quanto da receita NOVA (só a que vem do crescimento de contratos) vira folha.',
      onde: `Influencia ${pessoal}. ${tela} Sem crescimento de contratos cadastrado, não muda nada.`,
      como: 'Percentual × a receita extra do crescimento em cada mês, repartido entre as categorias de pessoal pelo peso de cada uma.',
    },
    crescimento: {
      oque: 'Quanto a carteira de contratos cresce (ou encolhe) por mês.',
      onde: `Influencia todas as receitas, o resultado, os pilares e, via "pessoal que acompanha a receita", a folha. ${tela}`,
      como: 'Percentual ao mês composto: cada mês multiplica a receita por (1 + %) uma vez a mais que o anterior.',
    },
    reajuste: {
      oque: 'O reajuste de preço dos contratos ao longo do ano.',
      onde: `Influencia todas as receitas. ${tela}`,
      como: 'Entra gradualmente, 1/12 por mês: em dezembro a receita está com o reajuste inteiro.',
    },
    recebimento: {
      oque: 'A parte do faturamento que não entra no caixa (inadimplência ou atraso).',
      onde: `Influencia todas as receitas. ${tela}`,
      como: 'Corta o percentual informado de todas as receitas, em todos os meses e cenários.',
    },
    inflacao: {
      oque: 'A inflação esperada nas despesas administrativas.',
      onde: `Influencia ${adm}. ${tela}`,
      como: 'Percentual ao ano aplicado mês a mês de forma crescente: em dezembro o reajuste está inteiro.',
    },
    custo_extra: {
      oque: 'Um custo novo proporcional à receita (um imposto a mais, uma comissão).',
      onde: `Cria a linha "Custo extra sobre a receita (premissa)" nas despesas, rateada pelos pilares na proporção da receita de cada um. ${tela} Não é gravada no orçamento.`,
      como: 'Receita de cada mês e cenário × os pontos percentuais informados.',
    },
    fixo: {
      oque: 'Um valor combinado por mês para a categoria (distribuição de lucros, pró-labore).',
      onde: `Trava a categoria com esse valor em todos os meses: ela vira decisão na grade. ${tela} Este valor É gravado no orçamento.`,
      como: 'O valor substitui a projeção da categoria nos 12 meses, igual em todos os cenários.',
    },
    investimentos: {
      oque: 'O total que a empresa pretende investir no ano.',
      onde: `Cria a linha "Investimentos (premissa)" nas despesas, no pilar Sem departamento. ${tela} Não é gravada no orçamento.`,
      como: '1/12 do total em cada mês, igual em todos os cenários.',
    },
    evento: {
      oque: 'Algo que pode ou não acontecer: um contrato novo, uma multa, uma contratação.',
      onde: `Cria uma linha própria (receita ou despesa) no pilar escolhido; aparece na grade, no resultado, na Simulação e na chance de prejuízo. ${tela} Não é gravada no orçamento.`,
      como: 'Entra em N % dos cenários (a chance), com o valor por mês a partir do mês inicial até dezembro.',
    },
  };
}

// botão "Remover" de uma premissa (volta ao neutro e grava pelo mesmo debounce)
function Remover({ rotulo, onRemover }) {
  return (
    <button type="button" className="op-prem-remover" onClick={onRemover} aria-label={`Remover ${rotulo}`} title={`Remover ${rotulo}`}>
      <i className="fas fa-trash me-1"></i>Remover
    </button>
  );
}

// menu "+ Adicionar premissa" com as premissas neutras do bloco
function Adicionar({ opcoes, onEscolher }) {
  if (!opcoes.length) return null;
  return (
    <select className="pa-input op-prem-add" value="" aria-label="Adicionar premissa"
      onChange={(e) => { if (e.target.value) onEscolher(e.target.value); }}>
      <option value="">+ Adicionar premissa</option>
      {opcoes.map((o) => <option key={o.id} value={o.id}>{o.rotulo}</option>)}
    </select>
  );
}

const arred1 = (v) => Math.round(v * 10) / 10;

export default function AbaPremissas({ clienteId, ano, proj, resultado, probPrejuizo, carregando, salvarPremissas, atualizar }) {
  const [rascunho, setRascunho] = useState(() => comPadrao(proj?.premissas));
  const [sujo, setSujo] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [falha, setFalha] = useState(null);          // {campo, erro}
  const [confirmar, setConfirmar] = useState(false);
  // premissas abertas pelo usuário: continuam à vista mesmo no valor neutro, até "Remover"
  const [abertos, setAbertos] = useState(() => new Set());
  // popover de ajuda aberto (um por vez)
  const [infoAberta, setInfoAberta] = useState(null);
  const fecharInfo = useCallback(() => setInfoAberta(null), []);
  const seq = useRef(0);                             // id do último POST
  const versao = useRef(0);                          // sobe a cada mudança do rascunho
  const chave = `${clienteId}|${ano}`;
  const chaveRascunho = useRef(chave);
  // funções do pai em ref: elas mudam a cada render e não podem reiniciar o debounce
  const salvarRef = useRef(salvarPremissas);
  const atualizarRef = useRef(atualizar);
  salvarRef.current = salvarPremissas;
  atualizarRef.current = atualizar;

  // troca de cliente/ano: o rascunho volta a ser o que o servidor mandou
  useEffect(() => {
    if (chaveRascunho.current !== chave) {
      chaveRascunho.current = chave;
      seq.current += 1; setSujo(false); setFalha(null); setAbertos(new Set());
      setRascunho(comPadrao(proj?.premissas));
    }
  }, [chave, proj]);

  const mudar = (fn) => {
    versao.current += 1;
    setRascunho((r) => { const n = copiar(r); fn(n); return n; }); setSujo(true);
  };
  const set = (k) => (v) => mudar((r) => { r[k] = v; });

  // debounce: 600 ms parado → grava o rascunho inteiro → refaz a projeção
  useEffect(() => {
    if (!sujo) return undefined;
    const local = checarLocal(rascunho);
    if (local) { setFalha(local); return undefined; }
    const t = setTimeout(async () => {
      const id = ++seq.current;
      const ver = versao.current;
      setGravando(true);
      const r = await salvarRef.current(normalizar(rascunho));
      if (id !== seq.current) return;              // já há um POST mais novo
      setGravando(false);
      // mudou de novo enquanto gravava: continua sujo e o próximo debounce grava
      if (ver === versao.current) setSujo(false);
      if (!r.ok) { setFalha({ campo: r.campo, erro: r.erro }); return; }
      setFalha(null);
      if (r.atual) atualizarRef.current();
    }, ESPERA);
    return () => clearTimeout(t);
  }, [rascunho, sujo]);

  const voltarPadrao = async () => {
    const id = ++seq.current;
    setGravando(true); setSujo(false);
    versao.current += 1;
    const r = await salvarRef.current({});
    if (id !== seq.current) return;
    setGravando(false); setConfirmar(false);
    if (!r.ok) { setFalha({ campo: r.campo, erro: r.erro }); return; }
    setFalha(null); setAbertos(new Set()); setRascunho(comPadrao(r.premissas));
    if (r.atual) atualizarRef.current();
  };

  if (!proj) return null;
  const R = rascunho;
  const ctx = proj.premissas_contexto || {};
  const grupos = proj.grupos_premissa || {};
  const erroDe = (campo) => (falha && falha.campo === campo ? falha.erro : null);
  const erroComeca = (pre) => (falha && String(falha.campo || '').startsWith(pre) ? falha.erro : null);

  // categorias de despesa reais (para os grupos e os nomes)
  const despesas = (proj.categorias || []).filter((c) => !ehSintetica(c));
  const nomeCat = Object.fromEntries(despesas.map((c) => [c.codigo, c.nome]));
  const marcadosPessoal = R.grupos?.pessoal ?? grupos.pessoal ?? [];
  const marcadosAdm = R.grupos?.administrativas ?? grupos.administrativas ?? [];
  // valor fixo: sugeridas + as que já têm valor gravado
  const mediaFixo = Object.fromEntries((ctx.fixos_media_12m || []).map((f) => [f.codigo, f]));
  const codFixos = [...new Set([...(grupos.fixos_sugeridos || []), ...Object.keys(R.valores_fixos || {})])];
  const nomeFixo = (cod) => mediaFixo[cod]?.nome || nomeCat[cod] || cod;
  // pilares para os eventos
  const pilares = [...(proj.pilares_simulados || []).filter((p) => p.codigo !== '').map((p) => ({ codigo: String(p.codigo), nome: p.nome })),
    { codigo: '', nome: SEM }];

  // Catálogo das premissas: ativa = fora do neutro; zerar = volta ao neutro;
  // iniciar = valor sugerido ao adicionar. Eventos são tratados à parte (lista).
  const temFixo = (cod) => Object.prototype.hasOwnProperty.call(R.valores_fixos || {}, cod);
  const cresc = Number(ctx.crescimento_receita_mes);
  const catalogo = {
    pessoal: [
      { id: 'dissidio', rotulo: 'Dissídio',
        ativa: (r) => r.dissidio_min > 0 || r.dissidio_max > 0 || Number(r.data_base_mes) !== 1,
        zerar: (r) => { r.dissidio_min = 0; r.dissidio_max = 0; r.data_base_mes = 1; },
        iniciar: (r) => { r.dissidio_min = 2; r.dissidio_max = 4.5; r.data_base_mes = 1; } },
      { id: 'acompanha', rotulo: 'Pessoal que acompanha a receita',
        ativa: (r) => r.pessoal_acompanha_receita > 0,
        zerar: (r) => { r.pessoal_acompanha_receita = 0; },
        iniciar: (r) => {
          const m = Number(ctx.pessoal_sobre_receita_12m);
          r.pessoal_acompanha_receita = Number.isFinite(m) ? Math.min(100, Math.max(0, Math.round(m / 5) * 5)) : 50;
        } },
    ],
    receita: [
      { id: 'crescimento', rotulo: 'Crescimento de contratos',
        ativa: (r) => r.crescimento_mes !== 0, zerar: (r) => { r.crescimento_mes = 0; },
        iniciar: (r) => { r.crescimento_mes = Number.isFinite(cresc) && cresc > 0 ? Math.min(10, arred1(cresc)) : 0.5; } },
      { id: 'reajuste', rotulo: 'Reajuste de preço no ano',
        ativa: (r) => r.reajuste_receita !== 0, zerar: (r) => { r.reajuste_receita = 0; }, iniciar: (r) => { r.reajuste_receita = 3; } },
      { id: 'recebimento', rotulo: 'Recebimento abaixo do histórico',
        ativa: (r) => r.recebimento_abaixo > 0, zerar: (r) => { r.recebimento_abaixo = 0; }, iniciar: (r) => { r.recebimento_abaixo = 2; } },
      { id: 'inflacao', rotulo: 'Inflação das administrativas',
        ativa: (r) => r.inflacao_administrativas > 0, zerar: (r) => { r.inflacao_administrativas = 0; }, iniciar: (r) => { r.inflacao_administrativas = 4; } },
    ],
    tributario: [
      { id: 'custo_extra', rotulo: 'Custo extra sobre a receita',
        ativa: (r) => r.custo_extra_pp > 0, zerar: (r) => { r.custo_extra_pp = 0; }, iniciar: (r) => { r.custo_extra_pp = 1; } },
      ...codFixos.map((cod) => ({
        id: `fixo:${cod}`, rotulo: `Valor fixo: ${nomeFixo(cod)}`,
        ativa: (r) => Object.prototype.hasOwnProperty.call(r.valores_fixos || {}, cod),
        zerar: (r) => { r.valores_fixos = { ...(r.valores_fixos || {}) }; delete r.valores_fixos[cod]; },
        iniciar: (r) => { r.valores_fixos = { ...(r.valores_fixos || {}), [cod]: Math.round(mediaFixo[cod]?.media_mensal_12m || 0) }; },
      })),
      { id: 'investimentos', rotulo: 'Investimentos no ano',
        ativa: (r) => r.investimentos_ano > 0, zerar: (r) => { r.investimentos_ano = 0; },
        iniciar: (r) => { r.investimentos_ano = Number.isFinite(ctx.investimentos_12m) ? Math.round(ctx.investimentos_12m) : 0; } },
    ],
  };
  const todas = Object.values(catalogo).flat();
  const porId = Object.fromEntries(todas.map((d) => [d.id, d]));
  const visivel = (id) => porId[id] && (porId[id].ativa(R) || abertos.has(id));
  const neutras = (bloco) => catalogo[bloco].filter((d) => !visivel(d.id));
  const adicionar = (id) => {
    const d = porId[id];
    if (!d) return;
    setAbertos((s) => new Set(s).add(id));
    mudar((r) => d.iniciar(r));
  };
  const remover = (id) => {
    const d = porId[id];
    if (!d) return;
    setAbertos((s) => { const t = new Set(s); t.delete(id); return t; });
    mudar((r) => d.zerar(r));
  };
  const novoEvento = () => mudar((r) => {
    r.eventos = [...(r.eventos || []), { nome: '', natureza: 'despesa', valor_mes: 0, mes_inicial: 1, chance: 50, pilar: '' }];
  });
  const algumaAtiva = todas.some((d) => visivel(d.id)) || (R.eventos || []).length > 0;

  const res10 = resultado.percentil(10), res50 = resultado.percentil(50), res90 = resultado.percentil(90);
  const ocupado = gravando || carregando || (sujo && !checarLocal(R));

  // ajuda de cada premissa (textos com os grupos reais)
  const ajuda = textosAjuda({ pessoal: marcadosPessoal, administrativas: marcadosAdm, nomeCat });
  // funções de render (não componentes): definidas dentro do render, um componente
  // novo a cada estado remontaria a subárvore e perderia o foco/popover
  const infoDe = (id, chave) => (
    <Info id={id} texto={ajuda[chave || id] || {}} aberto={infoAberta === id}
      onAlternar={() => setInfoAberta((a) => (a === id ? null : id))} onFechar={fecharInfo} />
  );
  // cabeçalho de uma premissa: título + ajuda + Remover
  const cab = (id, titulo) => (
    <div className="op-prem-cab">
      <span className="op-prem-titulo">
        {titulo || porId[id].rotulo}
        {infoDe(id, id.startsWith('fixo:') ? 'fixo' : id)}
      </span>
      <Remover rotulo={porId[id].rotulo} onRemover={() => remover(id)} />
    </div>
  );

  return (
    <div className="op-prem">
      <ConfirmarModal aberto={confirmar} titulo="Voltar ao padrão" rotuloConfirmar="Voltar ao padrão" processando={gravando}
        texto={<>Todas as premissas de {ano} são removidas (projeção só pelo histórico). Os valores digitados na grade não mudam.</>}
        onConfirmar={voltarPadrao} onCancelar={() => setConfirmar(false)} />

      {/* faixa grudada no topo: o efeito das premissas na hora */}
      <div className="op-prem-faixa">
        <div className="op-prem-kpi"><span>Resultado P10</span><b className={res10 < 0 ? 'neg' : ''}>{mi(res10)}</b></div>
        <div className="op-prem-kpi forte"><span>Resultado P50</span><b className={res50 < 0 ? 'neg' : ''}>{mi(res50)}</b></div>
        <div className="op-prem-kpi"><span>Resultado P90</span><b className={res90 < 0 ? 'neg' : ''}>{mi(res90)}</b></div>
        <div className="op-prem-kpi"><span>Chance de prejuízo</span><b>{probPrejuizo === null ? '—' : `${Math.round(probPrejuizo * 100)}%`}</b></div>
        <div className="op-prem-estado">
          {ocupado
            ? <span><i className="fas fa-spinner fa-spin me-1"></i>Recalculando…</span>
            : (falha ? <span className="op-prem-erro m-0"><i className="fas fa-circle-exclamation me-1"></i>{falha.erro}</span>
              : <span className="text-muted"><i className="fas fa-check me-1"></i>Premissas gravadas</span>)}
        </div>
        <button type="button" className="pa-btn pa-btn-ghost" onClick={() => setConfirmar(true)} disabled={gravando || !algumaAtiva}>
          <i className="fas fa-rotate-left me-1"></i>Voltar ao padrão
        </button>
      </div>
      <div className="op-prem-nota">As premissas valem para os 10.000 cenários da simulação. Os valores que você digitar na grade continuam valendo por cima.</div>
      {!algumaAtiva && (
        <div className="op-prem-vazio">
          <i className="fas fa-circle-info me-2"></i>Nenhuma premissa ativa — a projeção usa só o histórico. Use “+ Adicionar premissa” em um dos blocos.
        </div>
      )}

      <div className="op-prem-blocos">
        {/* PESSOAL */}
        <section className="pa-card op-prem-bloco">
          <div className="pa-card-body">
            <div className="pa-eyebrow mb-2 op-prem-bloco-t">Pessoal{infoDe('bloco_pessoal')}</div>
            {visivel('dissidio') && (
              <div className="op-prem-item">
                {cab('dissidio')}
                <Slider rotulo="Dissídio mínimo" valor={R.dissidio_min} min={0} max={15} passo={0.5} unidade="%"
                  erro={erroDe('dissidio_min')} onMudar={(v) => mudar((r) => { r.dissidio_min = v; })} />
                <Slider rotulo="Dissídio máximo" valor={R.dissidio_max} min={0} max={15} passo={0.5} unidade="%"
                  contexto="cada cenário sorteia um dissídio entre o mínimo e o máximo"
                  erro={erroDe('dissidio_max')} onMudar={(v) => mudar((r) => { r.dissidio_max = v; })} />
                <div className="op-prem-campo">
                  <div className="op-prem-linha"><label htmlFor="op-prem-db">Mês da data-base</label></div>
                  <select id="op-prem-db" className="pa-input" value={R.data_base_mes} onChange={(e) => set('data_base_mes')(parseInt(e.target.value, 10))}>
                    {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                  {erroDe('data_base_mes') && <div className="op-prem-erro">{erroDe('data_base_mes')}</div>}
                </div>
              </div>
            )}
            {visivel('acompanha') && (
              <div className="op-prem-item">
                {cab('acompanha')}
                <Slider rotulo="Pessoal que acompanha a receita" valor={R.pessoal_acompanha_receita} min={0} max={100} passo={5} unidade="%" casas={0}
                  contexto={Number.isFinite(ctx.pessoal_sobre_receita_12m) ? `pessoal é ${num(ctx.pessoal_sobre_receita_12m)} % da receita (últimos 12 meses)` : null}
                  erro={erroDe('pessoal_acompanha_receita')} onMudar={set('pessoal_acompanha_receita')} />
              </div>
            )}
            {(visivel('dissidio') || visivel('acompanha')) && (
              <>
                <Grupo titulo="pessoal" n={marcadosPessoal.length} categorias={despesas} marcados={marcadosPessoal}
                  onSalvar={(lista) => mudar((r) => { r.grupos = { ...(r.grupos || {}), pessoal: lista }; })} />
                {erroComeca('grupos.pessoal') && <div className="op-prem-erro">{erroComeca('grupos.pessoal')}</div>}
              </>
            )}
            {!visivel('dissidio') && !visivel('acompanha') && <div className="op-prem-ctx mb-2">Nenhuma premissa de pessoal.</div>}
            <Adicionar opcoes={neutras('pessoal')} onEscolher={adicionar} />
          </div>
        </section>

        {/* RECEITA E ESTRUTURA */}
        <section className="pa-card op-prem-bloco">
          <div className="pa-card-body">
            <div className="pa-eyebrow mb-2 op-prem-bloco-t">Receita e estrutura{infoDe('bloco_receita')}</div>
            {visivel('crescimento') && (
              <div className="op-prem-item">
                {cab('crescimento')}
                <Slider rotulo="Crescimento de contratos" valor={R.crescimento_mes} min={-5} max={10} passo={0.1} unidade="%/mês" comSinal
                  contexto={Number.isFinite(ctx.crescimento_receita_mes) ? `crescimento medido nos últimos 12 meses: ${sinal(ctx.crescimento_receita_mes, 2)} %/mês` : null}
                  erro={erroDe('crescimento_mes')} onMudar={set('crescimento_mes')} />
              </div>
            )}
            {visivel('reajuste') && (
              <div className="op-prem-item">
                {cab('reajuste')}
                <Slider rotulo="Reajuste de preço no ano" valor={R.reajuste_receita} min={-20} max={30} passo={0.5} unidade="%" comSinal
                  contexto={ctx.receita_12m ? `receita dos últimos 12 meses: ${reais(ctx.receita_12m)}` : null}
                  erro={erroDe('reajuste_receita')} onMudar={set('reajuste_receita')} />
              </div>
            )}
            {visivel('recebimento') && (
              <div className="op-prem-item">
                {cab('recebimento')}
                <Slider rotulo="Recebimento abaixo do histórico" valor={R.recebimento_abaixo} min={0} max={30} passo={0.5} unidade="%"
                  contexto={Number.isFinite(ctx.recebido_sobre_faturado) ? `recebido sobre faturado: ${num(ctx.recebido_sobre_faturado)} %` : 'parte do faturamento que não entra no caixa'}
                  erro={erroDe('recebimento_abaixo')} onMudar={set('recebimento_abaixo')} />
              </div>
            )}
            {visivel('inflacao') && (
              <div className="op-prem-item">
                {cab('inflacao')}
                <Slider rotulo="Inflação das administrativas" valor={R.inflacao_administrativas} min={0} max={20} passo={0.5} unidade="% a.a."
                  erro={erroDe('inflacao_administrativas')} onMudar={set('inflacao_administrativas')} />
                <Grupo titulo="administrativas" n={marcadosAdm.length} categorias={despesas} marcados={marcadosAdm}
                  onSalvar={(lista) => mudar((r) => { r.grupos = { ...(r.grupos || {}), administrativas: lista }; })} />
                {erroComeca('grupos.administrativas') && <div className="op-prem-erro">{erroComeca('grupos.administrativas')}</div>}
              </div>
            )}
            {!catalogo.receita.some((d) => visivel(d.id)) && <div className="op-prem-ctx mb-2">Nenhuma premissa de receita ou estrutura.</div>}
            <Adicionar opcoes={neutras('receita')} onEscolher={adicionar} />
          </div>
        </section>

        {/* TRIBUTÁRIO E SÓCIOS */}
        <section className="pa-card op-prem-bloco">
          <div className="pa-card-body">
            <div className="pa-eyebrow mb-2 op-prem-bloco-t">Tributário e sócios{infoDe('bloco_tributario')}</div>
            {visivel('custo_extra') && (
              <div className="op-prem-item">
                {cab('custo_extra')}
                <Slider rotulo="Custo extra sobre a receita" valor={R.custo_extra_pp} min={0} max={10} passo={0.1} unidade="p.p."
                  contexto={ctx.receita_12m ? `cada 1 p.p. ≈ ${reais(ctx.receita_12m * 0.01)}/ano na receita dos últimos 12 meses` : null}
                  erro={erroDe('custo_extra_pp')} onMudar={set('custo_extra_pp')} />
              </div>
            )}
            {codFixos.filter((cod) => visivel(`fixo:${cod}`)).map((cod) => {
              const media = mediaFixo[cod];
              const nome = nomeFixo(cod);
              return (
                <div key={cod} className="op-prem-item">
                  {cab(`fixo:${cod}`, <>Valor fixo por mês · {nome}</>)}
                  <CampoReais valor={temFixo(cod) ? R.valores_fixos[cod] : 0} rotulo={`Valor fixo de ${nome}`}
                    onMudar={(v) => mudar((r) => { r.valores_fixos = { ...(r.valores_fixos || {}), [cod]: v }; })} />
                  {media && <div className="op-prem-ctx">média dos últimos 12 meses: {reais(media.media_mensal_12m)}/mês</div>}
                  {erroDe(`valores_fixos.${cod}`) && <div className="op-prem-erro">{erroDe(`valores_fixos.${cod}`)}</div>}
                </div>
              );
            })}
            {visivel('investimentos') && (
              <div className="op-prem-item">
                {cab('investimentos')}
                <CampoReais valor={R.investimentos_ano} rotulo="Investimentos no ano" onMudar={set('investimentos_ano')} />
                {Number.isFinite(ctx.investimentos_12m) && <div className="op-prem-ctx">investimentos últimos 12 meses: {reais(ctx.investimentos_12m)}</div>}
                {erroDe('investimentos_ano') && <div className="op-prem-erro">{erroDe('investimentos_ano')}</div>}
              </div>
            )}
            {!catalogo.tributario.some((d) => visivel(d.id)) && <div className="op-prem-ctx mb-2">Nenhuma premissa tributária ou de sócios.</div>}
            <Adicionar opcoes={neutras('tributario')} onEscolher={adicionar} />
          </div>
        </section>

        {/* EVENTOS INCERTOS */}
        <section className="pa-card op-prem-bloco">
          <div className="pa-card-body">
            <div className="pa-eyebrow mb-2 op-prem-bloco-t">Eventos incertos{infoDe('bloco_eventos')}</div>
            <div className="op-prem-ctx mb-2">Algo que pode ou não acontecer: em cada cenário o sistema sorteia se ocorre, pela chance.</div>
            {(R.eventos || []).map((ev, i) => (
              <div key={i} className="op-prem-evento">
                <div className="op-prem-linha">
                  <input className="pa-input" placeholder="nome do evento" value={ev.nome} aria-label={`Nome do evento ${i + 1}`}
                    onChange={(e) => mudar((r) => { r.eventos[i].nome = e.target.value; })} />
                  {infoDe(`evento:${i}`, 'evento')}
                  <Remover rotulo={`o evento ${ev.nome || i + 1}`} onRemover={() => mudar((r) => { r.eventos.splice(i, 1); })} />
                </div>
                {erroComeca(`eventos[${i}]`) && <div className="op-prem-erro">{erroComeca(`eventos[${i}]`)}</div>}
                <div className="op-prem-ev-campos">
                  <select className="pa-input" value={ev.natureza} aria-label="Natureza"
                    onChange={(e) => mudar((r) => { r.eventos[i].natureza = e.target.value; })}>
                    <option value="despesa">Despesa</option>
                    <option value="receita">Receita</option>
                  </select>
                  <select className="pa-input" value={ev.mes_inicial} aria-label="A partir de"
                    onChange={(e) => mudar((r) => { r.eventos[i].mes_inicial = parseInt(e.target.value, 10); })}>
                    {MESES.map((m, k) => <option key={m} value={k + 1}>a partir de {m.toLowerCase()}</option>)}
                  </select>
                  <select className="pa-input" value={ev.pilar || ''} aria-label="Pilar"
                    onChange={(e) => mudar((r) => { r.eventos[i].pilar = e.target.value; })}>
                    {pilares.map((p) => <option key={p.codigo || '__sem__'} value={p.codigo}>{p.nome}</option>)}
                  </select>
                </div>
                <div className="op-prem-linha mt-1"><label>Valor por mês</label></div>
                <CampoReais valor={ev.valor_mes} rotulo={`Valor por mês do evento ${i + 1}`}
                  onMudar={(v) => mudar((r) => { r.eventos[i].valor_mes = v; })} />
                <Slider rotulo="Chance de acontecer" valor={ev.chance} min={0} max={100} passo={5} unidade="%" casas={0}
                  onMudar={(v) => mudar((r) => { r.eventos[i].chance = v; })} />
              </div>
            ))}
            {!(R.eventos || []).length && <div className="op-prem-ctx mb-2">Nenhum evento.</div>}
            {(R.eventos || []).length < EVENTOS_MAX
              ? <Adicionar opcoes={[{ id: 'evento', rotulo: 'Evento incerto' }]} onEscolher={novoEvento} />
              : <div className="op-prem-ctx">Limite de {EVENTOS_MAX} eventos.</div>}
            {erroDe('eventos') && <div className="op-prem-erro">{erroDe('eventos')}</div>}
          </div>
        </section>
      </div>
    </div>
  );
}

// Premissas na visão consolidada (somente leitura): cada empresa tem as suas.
// Lista só as ativas (fora do neutro) de cada uma e oferece abrir a empresa para editar.
function resumoPremissas(p, nomeCodigo) {
  if (!p) return [];
  const out = [];
  if (p.dissidio_min > 0 || p.dissidio_max > 0 || Number(p.data_base_mes) !== 1) {
    out.push(`Dissídio ${num(p.dissidio_min)}–${num(p.dissidio_max)} % a partir de ${MESES[(p.data_base_mes || 1) - 1].toLowerCase()}`);
  }
  if (p.pessoal_acompanha_receita > 0) out.push(`Pessoal acompanha a receita: ${num(p.pessoal_acompanha_receita, 0)} %`);
  if (p.crescimento_mes) out.push(`Crescimento de contratos: ${sinal(p.crescimento_mes)} %/mês`);
  if (p.reajuste_receita) out.push(`Reajuste de preço no ano: ${sinal(p.reajuste_receita)} %`);
  if (p.recebimento_abaixo > 0) out.push(`Recebimento abaixo do histórico: ${num(p.recebimento_abaixo)} %`);
  if (p.inflacao_administrativas > 0) out.push(`Inflação das administrativas: ${num(p.inflacao_administrativas)} % a.a.`);
  if (p.custo_extra_pp > 0) out.push(`Custo extra sobre a receita: ${num(p.custo_extra_pp)} p.p.`);
  Object.entries(p.valores_fixos || {}).forEach(([cod, v]) => out.push(`Valor fixo · ${nomeCodigo(cod)}: ${reais(v)}/mês`));
  if (p.investimentos_ano > 0) out.push(`Investimentos no ano: ${reais(p.investimentos_ano)}`);
  (p.eventos || []).forEach((e) => out.push(
    `Evento “${e.nome}” (${e.natureza}): ${reais(e.valor_mes)}/mês a partir de ${MESES[(e.mes_inicial || 1) - 1].toLowerCase()}, chance ${num(e.chance, 0)} %`));
  return out;
}

export function PremissasConsolidado({ proj, onAbrirEmpresa }) {
  const clientes = proj?.clientes || [];
  const porCliente = proj?.premissas_por_cliente || {};
  // nome de uma categoria pelo código local da empresa (vem das `origens` das linhas fundidas)
  const nomes = {};
  [...(proj?.categorias || []), ...(proj?.receitas?.categorias || [])].forEach((c) =>
    (c.origens || []).forEach((o) => { nomes[`${o.cliente_id}|${o.codigo}`] = c.nome; }));
  return (
    <div className="op-prem">
      <div className="op-prem-vazio">
        <i className="fas fa-circle-info me-2"></i>As premissas são de cada empresa — abra uma empresa para editar.
        A visão consolidada soma as duas projeções, cada uma com as suas premissas.
      </div>
      <div className="op-prem-blocos op-prem-consol">
        {clientes.map((cl) => {
          const linhas = resumoPremissas(porCliente[String(cl.id)], (cod) => nomes[`${cl.id}|${cod}`] || cod);
          return (
            <section key={cl.id} className="pa-card op-prem-bloco">
              <div className="pa-card-body">
                <div className="pa-eyebrow mb-2">{cl.nome}</div>
                {linhas.length
                  ? <ul className="op-prem-lista">{linhas.map((t) => <li key={t}>{t}</li>)}</ul>
                  : <div className="op-prem-ctx mb-2">Nenhuma premissa ativa — a projeção usa só o histórico.</div>}
                <button type="button" className="pa-btn pa-btn-ghost" onClick={() => onAbrirEmpresa(cl.id)}>
                  <i className="fas fa-arrow-up-right-from-square me-1"></i>Abrir {cl.nome}
                </button>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
